// بِسْمِ ٱللَّهِ ٱلرَّحْمَٰنِ ٱلرَّحِيمِ
//
// Шаблоны — campaign templates screen (B4). Mobile-first, in the warm Бриф
// design language (reuses the theme tokens + the ops screens' ScreenHeader /
// scroll / loading chrome, re-implemented locally so we never edit OpsScreens).
//
// Lists the 5 client-side STARTER_TEMPLATES plus any server templates from
// listTemplates() (soft read — the starters always render even offline). Tapping
// a card instantiates the template into a SourceContent (starters resolve
// client-side; server templates via instantiateTemplate) and seeds the chat
// composer with it, then navigates to the chat screen — from there the existing
// live pipeline (chatPlan → suggestions → …) takes over unchanged.
//
// We OWN this file. It imports from promo/templatesApi.ts, promo/types.ts and the
// controller's `useTemplate` action — nothing existing is edited.

import { useEffect, useState, type ReactNode } from 'react';
import {
  instantiateStarter,
  instantiateTemplate,
  isStarterTemplate,
  listTemplates,
  STARTER_TEMPLATES,
} from '../promo/templatesApi';
import type { Platform, PromotionTemplate, SourceContent } from '../promo/types';
import { platformLabel } from '../data';
import type { BriefController } from '../useBrief';
import { C, mono, SANS } from '../theme';

// ── Shared chrome (mirrors OpsScreens; kept local to avoid cross-file edits) ──

function ScreenHeader({ title, tag, mobile }: { title: string; tag?: string; mobile?: boolean }) {
  if (mobile) return null; // on mobile the section title lives in the top bar
  return (
    <div style={{ height: 56, flexShrink: 0, borderBottom: `1px solid ${C.line4}`, display: 'flex', alignItems: 'center', gap: 10, padding: '0 22px' }}>
      <div style={{ fontWeight: 600, fontSize: 14.5 }}>{title}</div>
      {tag && <div style={mono({ fontSize: 10, color: C.faint, border: `1px solid ${C.line}`, borderRadius: 6, padding: '2px 7px' })}>{tag}</div>}
    </div>
  );
}

function ScreenScroll({ children, mobile }: { children: ReactNode; mobile?: boolean }) {
  return (
    <div className="brief-scroll" style={{ flex: 1, overflowY: 'auto', padding: mobile ? '20px 16px 28px' : '24px 0' }}>
      <div style={{ maxWidth: mobile ? '100%' : 860, margin: '0 auto', padding: mobile ? 0 : '0 24px', animation: 'briefUp .3s ease both' }}>
        {children}
      </div>
    </div>
  );
}

function MonoLabel({ children }: { children: ReactNode }) {
  return <div style={mono({ fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint, margin: '0 0 10px' })}>{children}</div>;
}

function Loading({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.faint2, padding: '24px 0' }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 0.2, 0.4].map((d, i) => (
          <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: C.faint, display: 'inline-block', animation: `briefPulse 1.2s infinite ${d}s` }} />
        ))}
      </div>
      <span style={{ fontSize: 13 }}>{label}</span>
    </div>
  );
}

// ── Template card ────────────────────────────────────────────────

function TemplateCard({
  template,
  busy,
  onUse,
  mobile,
}: {
  template: PromotionTemplate;
  busy: boolean;
  onUse: () => void;
  mobile?: boolean;
}) {
  const platforms = template.platforms ?? [];
  const starter = isStarterTemplate(template);
  return (
    <button
      type="button"
      onClick={onUse}
      disabled={busy}
      style={{
        textAlign: 'left',
        border: `1px solid ${C.line}`,
        borderRadius: 14,
        padding: mobile ? '15px 16px' : '16px 17px',
        background: C.white,
        cursor: busy ? 'default' : 'pointer',
        fontFamily: SANS,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        transition: 'all .14s',
        opacity: busy ? 0.6 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 15.5, fontWeight: 700, letterSpacing: '-.01em', color: C.ink }}>{template.name}</div>
        <span style={mono({ fontSize: 9.5, letterSpacing: '.05em', textTransform: 'uppercase', color: starter ? C.faint : '#b08968', border: `1px solid ${starter ? C.line2 : '#e3d3c4'}`, borderRadius: 6, padding: '2px 6px', flexShrink: 0 })}>
          {starter ? 'шаблон' : 'сохранён'}
        </span>
      </div>
      {template.description && (
        <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.5 }}>{template.description}</div>
      )}
      {platforms.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {platforms.map((pl) => (
            <span key={pl} style={{ fontSize: 12, color: C.sub, background: '#f3f0eb', borderRadius: 7, padding: '4px 10px' }}>{platformLabel(pl as Platform)}</span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: C.ink2 }}>{busy ? 'Загружаю…' : 'Использовать шаблон'}</span>
        <span style={{ color: '#cfc9c0', fontSize: 16, marginLeft: 'auto' }}>›</span>
      </div>
    </button>
  );
}

// ── Screen ───────────────────────────────────────────────────────

export function TemplatesScreen({ controller, mobile }: { controller: BriefController; mobile?: boolean }) {
  const [server, setServer] = useState<PromotionTemplate[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Soft-load the server templates once. Starters render immediately regardless.
  useEffect(() => {
    let alive = true;
    listTemplates().then(
      (list) => alive && setServer(list),
      () => alive && setServer([]),
    );
    return () => {
      alive = false;
    };
  }, []);

  const use = async (template: PromotionTemplate) => {
    if (busyId) return;
    setBusyId(template.id);
    try {
      let source: SourceContent;
      if (isStarterTemplate(template)) {
        source = instantiateStarter(template, template.variables ?? {});
      } else {
        source = await instantiateTemplate(template.id, {});
      }
      controller.useTemplate(source);
    } catch (e) {
      controller.notify(e instanceof Error ? e.message : 'Не удалось загрузить шаблон');
    } finally {
      setBusyId(null);
    }
  };

  const serverTemplates = server ?? [];

  return (
    <>
      <ScreenHeader title="Шаблоны" tag="кампании" mobile={mobile} />
      <ScreenScroll mobile={mobile}>
        <p style={{ margin: '0 0 20px', fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
          Готовые заготовки постов. Выберите шаблон — его текст попадёт в поле ввода, а дальше ассистент соберёт кандидатов и черновики.
        </p>

        <div style={{ marginBottom: 24 }}>
          <MonoLabel>Стартовые</MonoLabel>
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            {STARTER_TEMPLATES.map((t) => (
              <TemplateCard key={t.id} template={t} busy={busyId === t.id} onUse={() => void use(t)} mobile={mobile} />
            ))}
          </div>
        </div>

        <div>
          <MonoLabel>Сохранённые</MonoLabel>
          {server === null ? (
            <Loading label="Загружаю шаблоны…" />
          ) : serverTemplates.length === 0 ? (
            <div style={{ fontSize: 14, color: C.muted }}>Пока нет сохранённых шаблонов. Стартовых наборов выше достаточно, чтобы начать.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 10 }}>
              {serverTemplates.map((t) => (
                <TemplateCard key={t.id} template={t} busy={busyId === t.id} onUse={() => void use(t)} mobile={mobile} />
              ))}
            </div>
          )}
        </div>
      </ScreenScroll>
    </>
  );
}

export default TemplatesScreen;
